import { ReactNode, useEffect, useRef, useState } from "react";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useEnv } from "~/hooks/use-env";
import type { PosthogBootstrap } from "~/.server/posthog";
import { synchronizePosthogIdentity } from "./posthog-identity";

type Props = {
  children: ReactNode;
  bootstrap?: PosthogBootstrap;
};

export const PosthogProvider = ({ children, bootstrap }: Props) => {
  const { POSTHOG_API_KEY } = useEnv();
  const [hydrated, setHydrated] = useState(false);
  const initializedKey = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!POSTHOG_API_KEY) return;

    let synchronizedDuringInitialization = false;
    if (initializedKey.current !== POSTHOG_API_KEY) {
      posthog.init(POSTHOG_API_KEY, {
        api_host: "/ph-relay",
        ui_host: "https://us.posthog.com",
        loaded: client => {
          // Read persisted ownership before changing identity, and synchronize
          // before the SDK captures its initial pageview.
          if (bootstrap) synchronizePosthogIdentity(client, bootstrap);
          synchronizedDuringInitialization = true;
        },
      });
      initializedKey.current = POSTHOG_API_KEY;
    }

    // init is a no-op when the singleton survives a provider remount.
    if (bootstrap && !synchronizedDuringInitialization) {
      synchronizePosthogIdentity(posthog, bootstrap);
    }

    setHydrated(true);
  }, [POSTHOG_API_KEY, bootstrap]);

  if (!hydrated) return <>{children}</>;
  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
};
